import { useReadQuery } from "@apollo/client";
import { QueryRef } from "@apollo/client/react/types/types";
import { Box, Stack } from "@mui/material";
import { GridSortModel } from "@mui/x-data-grid-premium";
import { TextCellWithTooltip } from "components/DataGridCell";
import { FilterGroup } from "components/Omnisearch/FilterPopover";
import {
  InvoiceAuditReportQuery,
  InvoiceAuditReportQueryVariables,
  InvoiceAuditReportSummaryQuery,
  InvoiceAuditReportSummaryQueryVariables,
} from "generated-graphql/graphql";
import { DataGridSkeleton } from "hooks/omnisearch-v2/DataGridSkeleton";
import { DataGridToolbar } from "hooks/omnisearch-v2/DataGridToolbar";
import { useOmnisearchActions, useOmnisearchStore } from "hooks/omnisearch-v2/omnisearchStore";
import { SuspenseDataGrid, SuspenseDataGridProps } from "hooks/omnisearch-v2/SuspenseDataGrid";
import { useGridPersistence } from "hooks/omnisearch-v2/useGridPersistence";
import { usePageForSearch } from "hooks/omnisearch-v2/usePageForSearch";
import { useBreadcrumb } from "hooks/useBreadcrumbs";
import { FilterKey, OmnisearchGridColDef } from "hooks/useOmnisearchDatagrid";
import { useTenant } from "hooks/useTenant";
import { ReactNode, Suspense, useCallback, useEffect, useMemo } from "react";

import { formatInvoiceDate } from "../utils";
import { AuditErrorBoundary } from "./AuditErrorBoundary";
import { auditInvoiceDateRange, withDefaultAuditInvoiceDate } from "./auditFilters";
import { AuditGridSection } from "./AuditGridSection";
import { AuditSummary } from "./auditUi";
import { formatAuditMoney } from "./formatAuditValues";
import { InvoiceAuditDateFilter } from "./InvoiceAuditDateFilter";
import { useInvoiceAuditReportQueries } from "./useInvoiceAuditQueries";

type ReportRow = InvoiceAuditReportQuery["wasteInvoiceChargeAuditReport"]["items"][number];
const INITIAL_SORT: GridSortModel = [{ field: "variance", sort: "desc" }];
const PERSISTENCE_KEY = "waste-invoice-audit-report";
const PAGE_SIZES = [25, 50, 100];
const FILTER_KEYS: FilterKey[] = [
  {
    key: "vendor",
    header: "Vendor",
    filterKeyType: "wasteInvoiceVendor",
  },
  { key: "invoiceNumber", header: "Invoice #" },
  { key: "facilityId", header: "Facility", filterKeyType: "facility" },
  { key: "billedAmount", header: "Billed amount", filterKeyType: "number" },
  { key: "totalOverages", header: "Overages", filterKeyType: "number" },
  { key: "totalUnderages", header: "Underages", filterKeyType: "number" },
  { key: "variance", header: "Net variance", filterKeyType: "number" },
];
const PRESERVED_FILTER_KEYS = ["invoiceId", "vendorId", "invoiceDate"];
const FILTER_GROUPS: FilterGroup[] = [
  { name: "Invoice", fields: ["vendor", "invoiceNumber"] },
  { name: "Location", fields: ["facilityId"] },
  {
    name: "Audit",
    fields: ["billedAmount", "totalOverages", "totalUnderages", "variance"],
  },
];
const COLUMNS: OmnisearchGridColDef<ReportRow>[] = [
  {
    field: "invoiceDate",
    headerName: "Invoice date",
    width: 125,
    type: "date",
    valueGetter: (_, row) =>
      row.invoice.invoiceDate
        ? new Date(`${row.invoice.invoiceDate.slice(0, 10)}T00:00:00Z`)
        : null,
    valueFormatter: (value: Date | null) => formatInvoiceDate(value?.toISOString()),
  },
  { field: "vendorName", headerName: "Vendor", minWidth: 150, flex: 1 },
  {
    field: "invoiceNumber",
    sortable: false,
    headerName: "Invoice #",
    width: 130,
    valueGetter: (_, row) => row.invoice.invoiceNumber,
  },
  {
    field: "facilities",
    headerName: "Facilities",
    minWidth: 240,
    flex: 1.5,
    valueGetter: (_, row) => row.facilities.join(", ") || "Not provided",
    renderCell: ({ value }) => <TextCellWithTooltip>{value}</TextCellWithTooltip>,
  },
  {
    field: "billedAmount",
    headerName: "Billed amount",
    width: 140,
    align: "right",
    headerAlign: "right",
    type: "number",
    valueGetter: (_, row) => row.totals.billedAmount,
    renderCell: ({ row }) => formatAuditMoney("billedAmount", row.totals.billedAmount),
  },
  {
    field: "totalOverages",
    headerName: "Overages",
    width: 165,
    align: "right",
    headerAlign: "right",
    type: "number",
    valueGetter: (_, row) => (row.totals.calculatedLineCount > 0 ? row.totals.totalOverages : null),
    renderCell: ({ row }) =>
      formatAuditMoney(
        "variance",
        row.totals.calculatedLineCount > 0 ? row.totals.totalOverages : null
      ),
  },
  {
    field: "totalUnderages",
    headerName: "Underages",
    width: 165,
    align: "right",
    headerAlign: "right",
    type: "number",
    valueGetter: (_, row) =>
      row.totals.calculatedLineCount > 0 ? row.totals.totalUnderages : null,
    renderCell: ({ row }) =>
      formatAuditMoney(
        "variance",
        row.totals.calculatedLineCount > 0 ? row.totals.totalUnderages : null
      ),
  },
  {
    field: "variance",
    headerName: "Net variance",
    width: 165,
    align: "right",
    headerAlign: "right",
    type: "number",
    valueGetter: (_, row) => (row.totals.calculatedLineCount > 0 ? row.totals.variance : null),
    renderCell: ({ row }) =>
      formatAuditMoney("variance", row.totals.calculatedLineCount > 0 ? row.totals.variance : null),
  },
];

type GridProps = Omit<
  SuspenseDataGridProps<InvoiceAuditReportQuery, InvoiceAuditReportQueryVariables, ReportRow>,
  "queryRef" | "getItems" | "getCount"
>;
const getItems = (data: InvoiceAuditReportQuery) => data.wasteInvoiceChargeAuditReport.items;
const getCount = (data: InvoiceAuditReportQuery) => data.wasteInvoiceChargeAuditReport.count;
const getRowId = (row: ReportRow) => row.invoice.id;

function ReportSummary({
  queryRef,
}: {
  queryRef: QueryRef<InvoiceAuditReportSummaryQuery, InvoiceAuditReportSummaryQueryVariables>;
}) {
  const { data } = useReadQuery(queryRef);
  return <AuditSummary summary={data.wasteInvoiceChargeAuditReportSummary.summary} />;
}

function ReportQuery({
  variables,
  gridProps,
  toolbar,
}: {
  variables: InvoiceAuditReportQueryVariables;
  gridProps: GridProps;
  toolbar: ReactNode;
}) {
  const {
    queryRef,
    retry,
    onRetry,
    summaryRef,
    summaryRetry,
    onSummaryRetry,
    isPending,
    summaryPending,
  } = useInvoiceAuditReportQueries(variables);
  return (
    <Stack spacing={3}>
      <Box sx={{ opacity: summaryPending ? 0.6 : 1 }}>
        <AuditErrorBoundary
          key={summaryRetry}
          resetKey={summaryRef}
          onRetry={onSummaryRetry}
          fallback={null}
        >
          <Suspense fallback={<AuditSummary />}>
            {summaryRef ? <ReportSummary queryRef={summaryRef} /> : <AuditSummary />}
          </Suspense>
        </AuditErrorBoundary>
      </Box>
      <AuditGridSection title="Invoices">
        {toolbar}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            flex: 1,
            minHeight: 0,
          }}
        >
          <AuditErrorBoundary key={retry} resetKey={queryRef} onRetry={onRetry}>
            <SuspenseDataGrid
              {...gridProps}
              queryRef={queryRef}
              isPending={isPending}
              getItems={getItems}
              getCount={getCount}
              fallback={<DataGridSkeleton />}
            />
          </AuditErrorBoundary>
        </Box>
      </AuditGridSection>
    </Stack>
  );
}

export function InvoiceAuditReport() {
  const { tenantId } = useTenant();
  const { omnisearch } = useOmnisearchStore();
  const actions = useOmnisearchActions();
  const searchWithDate = useMemo(() => withDefaultAuditInvoiceDate(omnisearch), [omnisearch]);
  const { setOmnisearchImmediate } = actions;
  useEffect(() => {
    if (searchWithDate !== omnisearch) setOmnisearchImmediate(searchWithDate);
  }, [omnisearch, searchWithDate, setOmnisearchImmediate]);
  const dateRange = useMemo(() => auditInvoiceDateRange(omnisearch), [omnisearch]);
  const hiddenDisplayTokens = useMemo(
    () => (dateRange.value ? [{ key: "invoiceDate", value: dateRange.value }] : []),
    [dateRange.value]
  );
  const persistence = useGridPersistence({
    persistenceKey: PERSISTENCE_KEY,
    initialSortModel: INITIAL_SORT,
    initialPageSize: 25,
    columns: COLUMNS,
  });
  const { page, setPage } = usePageForSearch(
    JSON.stringify([omnisearch, persistence.sortModel, persistence.pageSize])
  );
  const columns = useMemo(() => COLUMNS.map((column) => ({ ...column, filterable: false })), []);
  const variables = useMemo(
    (): InvoiceAuditReportQueryVariables => ({
      tenantId: tenantId ?? "",
      search: omnisearch,
      page,
      pageSize: persistence.pageSize,
      sort: persistence.sortModel.map(({ field, sort }) => ({ field, sort })),
    }),
    [tenantId, omnisearch, page, persistence.pageSize, persistence.sortModel]
  );
  const base = `/o/${tenantId}/waste/invoices`;
  const getRowLink = useCallback(
    (row: ReportRow) => `${base}/audit/${row.invoice.id}?${new URLSearchParams({ omnisearch })}`,
    [base, omnisearch]
  );
  const paginationModel = useMemo(
    () => ({ page, pageSize: persistence.pageSize }),
    [page, persistence.pageSize]
  );
  const { setPageSize } = persistence;
  const onPaginationModelChange = useCallback(
    (model: { page: number; pageSize: number }) => {
      setPage(model.page);
      setPageSize(model.pageSize);
    },
    [setPage, setPageSize]
  );
  useBreadcrumb([
    { label: "Waste", to: `/o/${tenantId}/waste` },
    { label: "Invoice Tracking", to: base },
    { label: "Audit" },
  ]);
  // Initialize the reporting period before mounting the query to avoid an all-time request.
  if (!tenantId || searchWithDate !== omnisearch) return null;
  const gridProps: GridProps = {
    columns,
    getRowId,
    getRowLink,
    paginationModel,
    onPaginationModelChange,
    sortModel: persistence.sortModel,
    onSortModelChange: persistence.setSortModel,
    columnVisibilityModel: persistence.columnVisibilityModel,
    onColumnVisibilityModelChange: persistence.setColumnVisibilityModel,
    pageSizeOptions: PAGE_SIZES,
    disableRowSelectionOnClick: true,
    noRowsMessage: "No invoices match this reporting period and filters.",
  };
  return (
    <>
      <InvoiceAuditDateFilter
        startDate={dateRange.startDate}
        endDate={dateRange.endDate}
        onAccept={({ startDate, endDate }) => {
          if (!startDate && !endDate) {
            const defaultRange = auditInvoiceDateRange(withDefaultAuditInvoiceDate(""));
            actions.editKeyValues([["invoiceDate", defaultRange.value]]);
          } else actions.editKeyValues([["invoiceDate", `${startDate ?? ""}..${endDate ?? ""}`]]);
        }}
      />
      <Box sx={{ p: 3, bgcolor: "canvas" }}>
        <ReportQuery
          variables={variables}
          gridProps={gridProps}
          toolbar={
            <DataGridToolbar
              compact
              tenantId={tenantId}
              persistenceKey={PERSISTENCE_KEY}
              omnisearchSource={{ omnisearch, ...actions }}
              columnFilterKeys={FILTER_KEYS}
              filterGroups={FILTER_GROUPS}
              preservedFilterKeys={PRESERVED_FILTER_KEYS}
              hiddenDisplayTokens={hiddenDisplayTokens}
              columns={columns}
              columnVisibilityModel={persistence.columnVisibilityModel}
              onColumnVisibilityModelChange={persistence.setColumnVisibilityModel}
            />
          }
        />
      </Box>
    </>
  );
}
